import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

export const EINZUGSTAGE = [1, 15];
export const datum = (iso) => (iso ? new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const eur = (v) => (typeof v === 'number' ? v.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) : '—');
export const LAUFZEITEN = [3, 6, 9, 12];
export const MIN_ANZAHLUNG_PROZENT = 25;
export const ZINSFREI_BIS_MONATE = 3;
export const MIN_GESAMT = 500;
export const istZinsfrei = (laufzeit) => Number(laufzeit) > 0 && Number(laufzeit) <= ZINSFREI_BIS_MONATE;
export const minAnzahlungProzent = (laufzeit) => (istZinsfrei(laufzeit) ? Math.round((100 / Number(laufzeit)) * 100) / 100 : MIN_ANZAHLUNG_PROZENT);
export const minAnzahlung = (gesamtbetrag, laufzeit = 6) => {
  const g = Number(gesamtbetrag) || 0;
  return Math.ceil((istZinsfrei(laufzeit) ? g / Number(laufzeit) : g * MIN_ANZAHLUNG_PROZENT / 100) * 100) / 100;
};

/* "2.500,50" / "2500,50" / "2500.50" → 2500.5 */
export const parseBetrag = (v) => {
  if (typeof v === 'number') return v;
  const s = String(v ?? '').replace(/[^\d.,]/g, '');
  if (!s) return 0;
  const n = s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : /^\d{1,3}(\.\d{3})+$/.test(s) ? s.replace(/\./g, '') : s;
  return Number(n) || 0;
};
const betragAnzeige = (v) => (v === '' || v === null || v === undefined ? '' : String(v).replace('.', ','));

/* Betragsfeld mit Komma- und Punkt-Eingabe; liefert dem Parent einen numerischen String (Punkt-Dezimal) oder ''. */
export const BetragInput = ({ value, onChange, className, ...rest }) => {
  const [txt, setTxt] = useState(() => betragAnzeige(value));
  useEffect(() => {
    if (parseBetrag(txt) !== (Number(value) || 0) || (value === '' && txt !== '')) setTxt(betragAnzeige(value));
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  const change = (e) => {
    const s = e.target.value.replace(/[^\d.,]/g, '');
    setTxt(s);
    onChange(s === '' ? '' : String(parseBetrag(s)));
  };
  return <input {...rest} className={className} type="text" inputMode="decimal" autoComplete="off" value={txt} onChange={change} />;
};

export const useAnzahlungMin = (gesamtbetrag, anzahlung, setAnzahlung, laufzeit = 6) => {
  useEffect(() => {
    const min = minAnzahlung(gesamtbetrag, laufzeit);
    if (min > 0 && (Number(anzahlung) || 0) < min) setAnzahlung(min);
  }, [gesamtbetrag, laufzeit]); // eslint-disable-line react-hooks/exhaustive-deps
};

const anzahlungHinweisText = (t, laufzeit, min) => (istZinsfrei(laufzeit)
  ? t('raten.form.anzahlung_min_zinsfrei', { n: Number(laufzeit), betrag: eur(min) })
  : t('raten.form.anzahlung_min', { prozent: MIN_ANZAHLUNG_PROZENT, betrag: eur(min) }));

export const AnzahlungHinweis = ({ gesamtbetrag, laufzeit = 6 }) => {
  const { t } = useTranslation();
  const min = minAnzahlung(gesamtbetrag, laufzeit);
  if (!min) return null;
  return <p className="text-[11px] text-[#7a8a99] mt-1" data-testid="raten-anzahlung-hinweis">{anzahlungHinweisText(t, laufzeit, min)}</p>;
};

/* Anzahlung: Mindestsatz (25 % bzw. 1/n bei zinsfreier Laufzeit) wird vorgeschlagen; frei änderbar, mindestens aber der Mindestsatz. */
export const AnzahlungFeld = ({ gesamtbetrag, anzahlung, laufzeit = 6, onChange, field, labelCls, label, testid = 'raten-anzahlung', dark = false }) => {
  const { t } = useTranslation();
  const [touched, setTouched] = useState(false);
  const min = minAnzahlung(gesamtbetrag, laufzeit);
  useEffect(() => {
    if (!touched || (Number(anzahlung) || 0) < min) onChange(min);
  }, [gesamtbetrag, laufzeit]); // eslint-disable-line react-hooks/exhaustive-deps
  const blur = () => { if ((Number(anzahlung) || 0) < min) onChange(min); };
  const zuWenig = (Number(anzahlung) || 0) < min;
  const zinsfrei = istZinsfrei(laufzeit);
  return (
    <div data-testid={testid}>
      <label className={labelCls}>{label || (zinsfrei ? t('raten.form.anzahlung_frei_zinsfrei', { n: Number(laufzeit) }) : t('raten.form.anzahlung_frei', { prozent: MIN_ANZAHLUNG_PROZENT }))}</label>
      <div className="relative">
        <BetragInput className={`${field} pr-10 ${zuWenig ? '!border-rose-400' : ''}`} value={anzahlung} onBlur={blur}
          onChange={(v) => { setTouched(true); onChange(v); }} data-testid={`${testid}-input`} />
        <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-sm ${dark ? 'text-white/60' : 'text-[#7a8a99]'}`}>€</span>
      </div>
      <p className={`text-[11px] mt-1.5 ${zuWenig ? 'text-rose-600' : dark ? 'text-white/60' : 'text-[#7a8a99]'}`} data-testid={`${testid}-hint`}>{anzahlungHinweisText(t, laufzeit, min)}</p>
    </div>
  );
};

export const rechnungsBereich = (nr) => (/^\d{1,6}\/\d{2}$/.test((nr || '').trim()) ? 'steuerberatung' : /^20\d{8}$/.test((nr || '').trim()) ? 'recht' : null);

export const RechnungsnummerFeld = ({ wert, onChange, required = true, label = true, testid = 'raten-rechnungsnummer', dark = false }) => {
  const { t } = useTranslation();
  const bereich = rechnungsBereich(wert);
  const invalid = !!(wert || '').trim() && !bereich;
  const field = `w-full px-3 py-2.5 bg-white border focus:outline-none text-sm rounded-sm ${invalid ? 'border-rose-400 focus:border-rose-500' : 'border-gray-200 focus:border-[#b8893d]'}`;
  return (
    <div>
      {label && <label className={`block text-[10px] tracking-wider uppercase mb-1.5 ${dark ? 'text-white/70' : 'text-[#7a8a99]'}`}>{t('raten.form.rechnungsnummer')}{required ? ' *' : ''}</label>}
      <input className={field} value={wert} onChange={(e) => onChange(e.target.value)} required={required} placeholder={t('raten.form.rechnungsnummer_placeholder')} pattern="(\d{1,6}/\d{2}|20\d{8})" title={t('raten.form.rechnungsnummer_format')} data-testid={testid} />
      <p className={`text-[11px] mt-1 ${invalid ? 'text-rose-600' : 'text-[#7a8a99]'}`} data-testid={`${testid}-hint`}>
        {bereich ? t(`raten.form.rechnungsnummer_${bereich}`) : t('raten.form.rechnungsnummer_format')}
      </p>
    </div>
  );
};

export const ReferenzFeld = ({ typ, wert, onTyp, onWert, required = true, disabled = false, className = '', testid = 'raten-referenz', dark = false }) => {
  const { t } = useTranslation();
  const field = 'w-full px-3 py-2.5 bg-white border border-gray-200 focus:border-[#b8893d] focus:outline-none text-sm rounded-sm disabled:bg-gray-50';
  return (
    <div className={className} data-testid={testid}>
      <div className="flex gap-1 mb-1.5" role="radiogroup">
        {['aktenzeichen', 'mandantennummer'].map((k) => (
          <button key={k} type="button" disabled={disabled} onClick={() => onTyp(k)} data-testid={`${testid}-typ-${k}`} aria-pressed={typ === k} className={`text-[10px] tracking-wider uppercase px-2.5 py-1 rounded-full border transition-colors ${typ === k ? 'bg-[#1B3A5C] text-white border-[#1B3A5C]' : `${dark ? 'bg-transparent text-white/80 border-white/30' : 'bg-white text-[#1B3A5C] border-gray-200'} hover:border-[#b8893d]`}`}>
            {t(`raten.form.referenz_${k}`)}{required ? ' *' : ''}
          </button>
        ))}
      </div>
      <input className={field} value={wert} onChange={(e) => onWert(typ === 'mandantennummer' ? e.target.value.replace(/\D/g, '').slice(0, 5) : e.target.value)} required={required} disabled={disabled} placeholder={t(`raten.form.referenz_${typ}_placeholder`)}
        inputMode={typ === 'mandantennummer' ? 'numeric' : undefined} pattern={typ === 'mandantennummer' ? '4\\d{4}' : undefined} title={typ === 'mandantennummer' ? t('raten.form.referenz_mandantennummer_format') : undefined} data-testid={`${testid}-wert`} />
    </div>
  );
};

export const ReferenzFelder = ({ aktenzeichen, mandantennummer, onAktenzeichen, onMandantennummer, testid = 'raten-referenz', dark = false }) => {
  const { t } = useTranslation();
  const field = 'w-full px-3 py-2.5 bg-white border border-gray-200 focus:border-[#b8893d] focus:outline-none text-sm rounded-sm';
  const lbl = `block text-[10px] tracking-wider uppercase mb-1.5 ${dark ? 'text-white/70' : 'text-[#7a8a99]'}`;
  const mnrUngueltig = !!mandantennummer && !/^4\d{4}$/.test(mandantennummer);
  return (
    <>
      <div data-testid={`${testid}-aktenzeichen`}>
        <label className={lbl}>{t('raten.form.referenz_aktenzeichen')} ({t('raten.form.optional')})</label>
        <input className={field} value={aktenzeichen} onChange={(e) => onAktenzeichen(e.target.value)} placeholder={t('raten.form.referenz_aktenzeichen_placeholder')} maxLength={60} data-testid={`${testid}-aktenzeichen-wert`} />
      </div>
      <div data-testid={`${testid}-mandantennummer`}>
        <label className={lbl}>{t('raten.form.referenz_mandantennummer')} ({t('raten.form.optional')})</label>
        <input className={`${field} ${mnrUngueltig ? '!border-rose-400' : ''}`} value={mandantennummer} onChange={(e) => onMandantennummer(e.target.value.replace(/\D/g, '').slice(0, 5))} placeholder={t('raten.form.referenz_mandantennummer_placeholder')}
          inputMode="numeric" pattern={'4\\d{4}'} title={t('raten.form.referenz_mandantennummer_format')} data-testid={`${testid}-mandantennummer-wert`} />
        <p className={`text-[11px] mt-1 ${mnrUngueltig ? 'text-rose-600' : dark ? 'text-white/60' : 'text-[#7a8a99]'}`}>{t('raten.form.referenz_mandantennummer_format')}</p>
      </div>
    </>
  );
};

export const useRatenVorschau = (gesamtbetrag, anzahlung, laufzeit, einzugstag = 1) => {
  const [vorschau, setVorschau] = useState(null);
  const [fehler, setFehler] = useState('');
  useEffect(() => {
    const g = Number(gesamtbetrag) || 0;
    const a = Number(anzahlung) || 0;
    if (g < MIN_GESAMT || a >= g) { setVorschau(null); return undefined; }
    const h = setTimeout(async () => {
      try { const r = await axios.post(`${API}/raten/berechnen`, { gesamtbetrag: g, anzahlung: a, laufzeit_monate: laufzeit, einzugstag }); setVorschau(r.data); setFehler(''); }
      catch (e) { const d = e?.response?.data?.detail; setVorschau(null); setFehler(Array.isArray(d) ? d.map((x) => x.msg).join(', ') : (typeof d === 'string' ? d : '')); }
    }, 300);
    return () => clearTimeout(h);
  }, [gesamtbetrag, anzahlung, laufzeit, einzugstag]);
  return { vorschau, fehler };
};

export const EinzugstagChips = ({ value, onChange, testid = 'raten-einzugstag', dark = false }) => {
  const { t } = useTranslation();
  return (
    <div data-testid={testid}>
      <div className="flex gap-2">
        {EINZUGSTAGE.map((d) => (
          <button key={d} type="button" onClick={() => onChange(d)} data-testid={`${testid}-${d}`} aria-pressed={value === d}
            className={`text-xs px-4 py-2 rounded-sm border transition-colors ${value === d ? 'bg-[#1B3A5C] text-white border-[#1B3A5C]' : `${dark ? 'bg-transparent text-white/80 border-white/30' : 'bg-white text-[#1B3A5C] border-gray-200'} hover:border-[#b8893d]`}`}>
            {t('raten.form.einzugstag_wert', { tag: d })}
          </button>
        ))}
      </div>
      <p className={`text-[11px] mt-1 ${dark ? 'text-white/60' : 'text-[#7a8a99]'}`}>{t('raten.form.einzugstag_hint')}</p>
    </div>
  );
};

export const Zahlungsplan = ({ b, raten, compact = false, testid = 'raten-zahlungsplan' }) => {
  const { t } = useTranslation();
  if (!b) return null;
  const rows = [];
  if (b.anzahlung) rows.push({ k: 'anz', pos: t('raten.zahlungsplan.anzahlung'), datum: t('raten.zahlungsplan.bei_einrichtung'), betrag: b.anzahlung });
  if (b.bearbeitungsgebuehr) rows.push({ k: 'geb', pos: t('raten.zahlungsplan.gebuehr'), datum: t('raten.zahlungsplan.bei_einrichtung'), betrag: b.bearbeitungsgebuehr });
  (raten || b.plan || []).forEach((r) => rows.push({ k: r.nr, pos: t('raten.zahlungsplan.rate', { nr: r.nr }), datum: datum(r.faellig_am), betrag: r.betrag ?? r.rate, status: r.status }));
  return (
    <div data-testid={testid}>
      <div className="text-[10px] tracking-[0.25em] text-[#b8893d] uppercase mb-2">{t('raten.zahlungsplan.titel')}</div>
      <table className={`w-full ${compact ? 'text-xs' : 'text-sm'}`}>
        <thead><tr className="text-[10px] tracking-wider text-[#7a8a99] text-left"><th className="pb-1 font-medium">{t('raten.zahlungsplan.position')}</th><th className="pb-1 font-medium">{t('raten.zahlungsplan.faellig')}</th><th className="pb-1 font-medium text-right">{t('raten.zahlungsplan.betrag')}</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.k} className="border-t border-gray-100" data-testid={`${testid}-row-${r.k}`}>
              <td className="py-1.5 text-[#1B3A5C]">{r.pos}{r.status === 'bezahlt' && <span className="ml-2 text-[10px] text-emerald-700">✓</span>}</td>
              <td className="py-1.5 text-[#34536b]">{r.datum}</td>
              <td className="py-1.5 text-right font-medium text-[#1B3A5C]">{eur(r.betrag)}</td>
            </tr>
          ))}
          <tr className="border-t border-[#1B3A5C]/30"><td className="py-1.5 font-semibold text-[#1B3A5C]" colSpan={2}>{t('raten.zahlungsplan.gesamt')}</td><td className="py-1.5 text-right font-semibold text-[#1B3A5C]">{eur(b.gesamtkosten)}</td></tr>
        </tbody>
      </table>
    </div>
  );
};

export const KonditionenTabelle = ({ b, compact, testid = 'raten-konditionen' }) => {
  const { t } = useTranslation();
  if (!b) return null;
  const rows = [
    [t('raten.kond.gesamtbetrag'), eur(b.gesamtbetrag)],
    b.anzahlung ? [t('raten.kond.anzahlung'), eur(b.anzahlung)] : null,
    [t('raten.kond.laufzeit'), t('raten.kond.laufzeit_wert', { monate: b.laufzeit_monate, raten: b.anzahl_raten ?? b.laufzeit_monate })],
    [t('raten.kond.rate'), b.letzte_rate && b.letzte_rate !== b.rate ? t('raten.kond.rate_letzte', { rate: eur(b.rate), letzte: eur(b.letzte_rate) }) : eur(b.rate), true],
    b.zinsfrei
      ? [t('raten.kond.zins'), t('raten.kond.zinsfrei')]
      : [t('raten.kond.zins'), t('raten.kond.zins_wert', { soll: b.zinssatz.toFixed(2).replace('.', ','), jahr: eur(b.zins_pro_jahr ?? 0), monat: eur(b.zins_pro_monat ?? 0), monate: b.anzahl_raten ?? b.laufzeit_monate, eff: b.effektiver_jahreszins.toFixed(2).replace('.', ',') })],
    b.zinsfrei ? null : [t('raten.kond.zinsen_gesamt'), eur(b.zinsen_gesamt)],
    b.bearbeitungsgebuehr ? [t('raten.kond.gebuehr'), eur(b.bearbeitungsgebuehr)] : null,
    b.bearbeitungsgebuehr ? [t('raten.kond.sofort_faellig'), eur(b.sofort_faellig)] : null,
    b.einzugstag ? [t('raten.kond.einzug'), t('raten.kond.einzug_wert', { tag: b.einzugstag, datum: datum(b.erste_rate_am) })] : null,
    [t('raten.kond.gesamtkosten'), eur(b.gesamtkosten), true],
  ].filter(Boolean);
  return (
    <table className={`w-full ${compact ? 'text-xs' : 'text-sm'}`} data-testid={testid}>
      <tbody>
        {rows.map(([k, v, strong]) => (
          <tr key={k} className="border-b border-gray-100 last:border-0">
            <td className="py-1.5 text-[#7a8a99]">{k}</td>
            <td className={`py-1.5 text-right ${strong ? 'font-semibold text-[#1B3A5C]' : 'text-[#34536b]'}`}>{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const LaufzeitChips = ({ value, onChange, testid = 'raten-laufzeit' }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {LAUFZEITEN.map((m) => (
        <button key={m} type="button" onClick={() => onChange(m)} data-testid={`${testid}-${m}`} aria-pressed={value === m} className={`text-xs px-3 py-1.5 rounded-full border transition-colors inline-flex items-center gap-1.5 ${value === m ? 'bg-[#1B3A5C] text-white border-[#1B3A5C]' : 'bg-white text-[#1B3A5C] border-gray-200 hover:border-[#b8893d]'}`}>
          {t('raten.kond.monate', { count: m })}
          {istZinsfrei(m) && <span className={`text-[9px] tracking-wider uppercase px-1.5 py-px rounded-full ${value === m ? 'bg-[#e0b56a] text-[#1B3A5C]' : 'bg-emerald-50 text-emerald-700'}`} data-testid={`${testid}-${m}-zinsfrei`}>{t('raten.kond.zinsfrei_badge')}</span>}
        </button>
      ))}
    </div>
  );
};

export const RatenStatusChip = ({ status, label }) => {
  const cls = {
    angefragt: 'bg-sky-50 text-sky-800 border-sky-200', angeboten: 'bg-amber-50 text-amber-800 border-amber-200', aktiv: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    ueberfaellig: 'bg-rose-50 text-rose-700 border-rose-200', abgeschlossen: 'bg-gray-100 text-gray-600 border-gray-200', storniert: 'bg-gray-100 text-gray-500 border-gray-200', inkasso: 'bg-rose-100 text-rose-800 border-rose-300',
  }[status] || 'bg-gray-100 text-gray-600 border-gray-200';
  return <span className={`text-[10px] tracking-wider uppercase px-2 py-0.5 border rounded-full ${cls}`} data-testid="raten-status">{label || status}</span>;
};

export const RatenFortschritt = ({ raten }) => (
  <div className="flex gap-1 flex-wrap" data-testid="raten-fortschritt">
    {raten.map((r) => (
      <span key={r.nr} title={`Rate ${r.nr}: ${r.status}`} className={`w-5 h-2 rounded-sm ${r.status === 'bezahlt' ? 'bg-emerald-500' : r.status === 'fehlgeschlagen' ? 'bg-rose-500' : 'bg-gray-200'}`} />
    ))}
  </div>
);

export const Spinner = () => <Loader2 className="w-5 h-5 animate-spin text-[#b8893d]" />;
